import { create } from 'zustand';
import type { SelectedItemInfo, OrderItem, AddressInfo, PriceBreakdown } from '@/types';

interface OrderStore {
  // Step 1: 물품
  itemInfo: SelectedItemInfo;
  items: OrderItem[];

  // Step 2: 주소
  origin: AddressInfo | null;
  destination: AddressInfo | null;

  // Step 3: 운송비
  priceBreakdown: PriceBreakdown | null;
  distanceKm: number | null;

  // Step 4: 일정
  scheduleDate: string | null;
  designatedPickupSlot: string | null;
  designatedDeliverySlot: string | null;
  flexiblePickupMorning: boolean;
  flexiblePickupAfternoon: boolean;
  flexibleDeliveryMorning: boolean;
  flexibleDeliveryAfternoon: boolean;
  flexibleDeliveryAnytime: boolean;
  showDeliveryTimePanel: boolean;
  pickupDiscountRate: number;  // 0, 5, 7, 10
  deliveryDiscountRate: number;

  // Step 5: 결제
  phone: string;
  originPhone: string;
  additionalNote: string;
  termsAgreed: {
    terms: boolean;
    privacy: boolean;
    marketing: boolean;
  };

  // 쿠폰
  appliedCouponCode: string | null;
  couponDiscount: number;

  // 견적
  quoteId: string | null;

  // UI
  currentStep: number;
  totalSteps: number;

  // 액션
  setItemInfo: (data: Partial<SelectedItemInfo>) => void;
  setOrigin: (data: AddressInfo | null) => void;
  setDestination: (data: AddressInfo | null) => void;
  setPriceBreakdown: (data: PriceBreakdown | null) => void;
  setDistanceKm: (km: number | null) => void;
  setSchedule: (data: {
    date?: string;
    pickupSlot?: string | null;
    deliverySlot?: string | null;
    flexPickupMorning?: boolean;
    flexPickupAfternoon?: boolean;
    flexDeliveryMorning?: boolean;
    flexDeliveryAfternoon?: boolean;
    flexDeliveryAnytime?: boolean;
    showDeliveryPanel?: boolean;
    pickupDiscount?: number;
    deliveryDiscount?: number;
  }) => void;
  setPhone: (phone: string) => void;
  setOriginPhone: (phone: string) => void;
  setAdditionalNote: (note: string) => void;
  setTermsAgreed: (key: 'terms' | 'privacy' | 'marketing', value: boolean) => void;
  setAllTermsAgreed: (value: boolean) => void;
  addItem: () => void;
  removeItem: (uid: string) => void;
  setCoupon: (code: string | null, discount: number) => void;
  setQuoteId: (quoteId: string | null) => void;
  setCurrentStep: (step: number, total?: number) => void;
  reset: () => void;
}

const initialItemInfo: SelectedItemInfo = {
  itemId: null,
  category: null,
  options: {},
  basePrice: 0,
  isCustom: false,
};

const initialTermsAgreed = {
  terms: false,
  privacy: false,
  marketing: false,
};

export const useOrderStore = create<OrderStore>((set) => ({
  // 초기 상태
  itemInfo: initialItemInfo,
  items: [],
  origin: null,
  destination: null,
  priceBreakdown: null,
  distanceKm: null,
  scheduleDate: null,
  designatedPickupSlot: null,
  designatedDeliverySlot: null,
  flexiblePickupMorning: false,
  flexiblePickupAfternoon: false,
  flexibleDeliveryMorning: false,
  flexibleDeliveryAfternoon: false,
  flexibleDeliveryAnytime: false,
  showDeliveryTimePanel: false,
  pickupDiscountRate: 0,
  deliveryDiscountRate: 0,
  phone: '',
  originPhone: '',
  additionalNote: '',
  termsAgreed: initialTermsAgreed,
  appliedCouponCode: null,
  couponDiscount: 0,
  quoteId: null,
  currentStep: 1,
  totalSteps: 5,

  // 액션 구현
  setItemInfo: (data) =>
    set((state) => ({
      itemInfo: { ...state.itemInfo, ...data },
    })),

  setOrigin: (data) => set({ origin: data }),

  setDestination: (data) => set({ destination: data }),

  setPriceBreakdown: (data) => set({ priceBreakdown: data }),

  setDistanceKm: (km) => set({ distanceKm: km }),

  setSchedule: (data) =>
    set((state) => ({
      scheduleDate: data.date !== undefined ? data.date : state.scheduleDate,
      designatedPickupSlot: data.pickupSlot !== undefined ? data.pickupSlot : state.designatedPickupSlot,
      designatedDeliverySlot: data.deliverySlot !== undefined ? data.deliverySlot : state.designatedDeliverySlot,
      flexiblePickupMorning: data.flexPickupMorning !== undefined ? data.flexPickupMorning : state.flexiblePickupMorning,
      flexiblePickupAfternoon: data.flexPickupAfternoon !== undefined ? data.flexPickupAfternoon : state.flexiblePickupAfternoon,
      flexibleDeliveryMorning: data.flexDeliveryMorning !== undefined ? data.flexDeliveryMorning : state.flexibleDeliveryMorning,
      flexibleDeliveryAfternoon: data.flexDeliveryAfternoon !== undefined ? data.flexDeliveryAfternoon : state.flexibleDeliveryAfternoon,
      flexibleDeliveryAnytime: data.flexDeliveryAnytime !== undefined ? data.flexDeliveryAnytime : state.flexibleDeliveryAnytime,
      showDeliveryTimePanel: data.showDeliveryPanel !== undefined ? data.showDeliveryPanel : state.showDeliveryTimePanel,
      pickupDiscountRate: data.pickupDiscount !== undefined ? data.pickupDiscount : state.pickupDiscountRate,
      deliveryDiscountRate: data.deliveryDiscount !== undefined ? data.deliveryDiscount : state.deliveryDiscountRate,
    })),

  setPhone: (phone) => set({ phone }),

  setOriginPhone: (phone) => set({ originPhone: phone }),

  setAdditionalNote: (note) => set({ additionalNote: note }),

  setTermsAgreed: (key, value) =>
    set((state) => ({
      termsAgreed: { ...state.termsAgreed, [key]: value },
    })),

  setAllTermsAgreed: (value) =>
    set({
      termsAgreed: { terms: value, privacy: value, marketing: value },
    }),

  addItem: () =>
    set((state) => {
      if (!state.itemInfo.itemId) return state;
      const newItem: OrderItem = {
        ...state.itemInfo,
        uid: `item_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        displayName: state.itemInfo.category || '물품',
      };
      return {
        items: [...state.items, newItem],
        itemInfo: initialItemInfo,
      };
    }),

  removeItem: (uid) =>
    set((state) => ({
      items: state.items.filter((item) => item.uid !== uid),
    })),

  setCoupon: (code, discount) => set({ appliedCouponCode: code, couponDiscount: discount }),

  setQuoteId: (quoteId) => set({ quoteId }),

  setCurrentStep: (step, total) => set((state) => ({
    currentStep: step,
    ...(total !== undefined ? { totalSteps: total } : {}),
  })),

  reset: () =>
    set({
      itemInfo: initialItemInfo,
      items: [],
      origin: null,
      destination: null,
      priceBreakdown: null,
      distanceKm: null,
      scheduleDate: null,
      designatedPickupSlot: null,
      designatedDeliverySlot: null,
      flexiblePickupMorning: false,
      flexiblePickupAfternoon: false,
      flexibleDeliveryMorning: false,
      flexibleDeliveryAfternoon: false,
      flexibleDeliveryAnytime: false,
      showDeliveryTimePanel: false,
      pickupDiscountRate: 0,
      deliveryDiscountRate: 0,
      phone: '',
      additionalNote: '',
      termsAgreed: initialTermsAgreed,
      appliedCouponCode: null,
      couponDiscount: 0,
      quoteId: null,
      currentStep: 1,
      totalSteps: 5,
    }),
}));
